import { useEffect, useRef, useState } from 'react'
import { adsenseClient, adsenseSlot, isAdsEnabled, isAdSlotConfigured } from '../runtime/adsConfig'

declare global {
  interface Window {
    adsbygoogle?: unknown[]
  }
}

const SCRIPT_ID = 'google-adsense-script'
const SCRIPT_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'

/**
 * Loads the AdSense script once, asynchronously and non-blocking.
 * Returns a promise that resolves when the script is ready, rejects on failure.
 */
function loadAdSenseScript(client: string): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('no document'))

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    if (existing.dataset.loaded === 'true' || typeof window.adsbygoogle !== 'undefined') {
      existing.dataset.loaded = 'true'
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener(
        'load',
        () => {
          existing.dataset.loaded = 'true'
          resolve()
        },
        { once: true }
      )
      existing.addEventListener('error', () => reject(new Error('adsense load error')), {
        once: true
      })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = `${SCRIPT_SRC}?client=${encodeURIComponent(client)}`
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve()
      },
      { once: true }
    )
    script.addEventListener('error', () => reject(new Error('adsense load error')), { once: true })
    document.head.appendChild(script)
  })
}

interface AdSlotProps {
  /** Fallback content shown when ads are disabled or delivery fails. */
  fallback: React.ReactNode
}

/**
 * Renders a Google AdSense unit on non-critical screens, gated behind the
 * global feature flag. When ads are disabled or fail to load, the provided
 * fallback (e.g. the sponsor placeholder) is shown instead.
 */
export function AdSlot({ fallback }: AdSlotProps): React.JSX.Element {
  const insRef = useRef<HTMLModElement | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!isAdsEnabled || !isAdSlotConfigured || !adsenseClient) return
    let cancelled = false

    loadAdSenseScript(adsenseClient)
      .then(() => {
        if (cancelled) return
        if (insRef.current?.getAttribute('data-adsbygoogle-status')) return
        try {
          ;(window.adsbygoogle = window.adsbygoogle ?? []).push({})
        } catch {
          setFailed(true)
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (!isAdsEnabled || !isAdSlotConfigured || failed) {
    return (
      <div className="ad-slot-frame">
        <div className="ad-slot-label">Sponsored content</div>
        {fallback}
      </div>
    )
  }

  return (
    <div className="ad-slot-frame">
      <div className="ad-slot-label">Sponsored content</div>
      <ins
        ref={insRef}
        className="adsbygoogle ad-slot"
        style={{ display: 'block' }}
        data-ad-client={adsenseClient}
        data-ad-slot={adsenseSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  )
}
